<template>
  <div
    class="ui-select"
    :class="[$attrs.class, `ui-select--${size}`, { 'ui-select--error': hasError, 'ui-select--inverted': inverted, 'ui-select--borderless': borderless }]"
    :style="$attrs.style"
  >
    <Label v-if="label" :for="resolvedId" class="ui-select__label">
      {{ label }}
      <span v-if="required" aria-hidden="true">*</span>
      <slot name="labelTooltip" />
    </Label>
    <SelectRoot v-model="value" :disabled="disabled" :required="required">
      <SelectTrigger
        :id="resolvedId"
        v-bind="nativeAttrs"
        class="ui-select__button"
        :aria-describedby="message ? messageId : undefined"
        :aria-invalid="hasError || undefined"
      >
        <span v-if="$slots.prefix" class="ui-select__prefix"><slot name="prefix" /></span>
        <SelectValue class="ui-select__value" :placeholder="placeholder" />
        <SelectIcon as-child>
          <Icon class="ui-select__chevron" name="ChevronDown" :size="20" />
        </SelectIcon>
      </SelectTrigger>
      <SelectContent class="ui-select__options" position="popper" :side-offset="4">
        <SelectViewport class="ui-select__viewport">
          <RekaSelectItem
            v-for="item in items"
            :key="item.id"
            class="ui-select__option"
            :disabled="item.disabled"
            :text-value="item.label"
            :value="item.id"
          >
            <span>
              <SelectItemText as="strong">{{ item.label }}</SelectItemText>
              <small v-if="item.description">{{ item.description }}</small>
            </span>
            <SelectItemIndicator as-child><Icon name="Check" :size="20" /></SelectItemIndicator>
          </RekaSelectItem>
          <div v-if="items.length === 0" class="ui-select__empty">
            <slot name="empty">Brak wyników</slot>
          </div>
        </SelectViewport>
      </SelectContent>
    </SelectRoot>
    <p v-if="message" :id="messageId" class="ui-select__message">{{ message }}</p>
  </div>
</template>
<script setup lang="ts">
  import {
    Label,
    SelectItem as RekaSelectItem,
    SelectContent,
    SelectIcon,
    SelectItemIndicator,
    SelectItemText,
    SelectRoot,
    SelectTrigger,
    SelectValue,
    SelectViewport,
  } from 'reka-ui'
  import { computed, useAttrs, useId } from 'vue'

  import { Icon } from '../Icon'
  import type { SelectItem } from '../shared/types'

  defineOptions({ inheritAttrs: false, name: 'UiSelect' })
  const props = withDefaults(
    defineProps<{
      borderless?: boolean
      disabled?: boolean
      error?: boolean | string
      helperText?: string
      id?: string
      inverted?: boolean
      items: SelectItem[]
      label?: string
      placeholder?: string
      required?: boolean
      size?: 'md' | 'sm'
    }>(),
    { size: 'md' },
  )
  const value = defineModel<number | string | null>({ default: null })
  const attrs = useAttrs()
  const generatedId = useId()
  const resolvedId = computed(() => props.id ?? `ui-select-${generatedId}`)
  const messageId = computed(() => `${resolvedId.value}-message`)
  const hasError = computed(
    () => props.error === true || (typeof props.error === 'string' && props.error.length > 0),
  )
  const message = computed(() =>
    typeof props.error === 'string' && props.error ? props.error : props.helperText,
  )
  const nativeAttrs = computed(() =>
    Object.fromEntries(Object.entries(attrs).filter(([key]) => key !== 'class' && key !== 'style')),
  )
</script>
<style scoped lang="scss">
@use '@repo/ui/typography.scss' as typography;

/* Open, disabled, error and focus states intentionally override hover. */
/* stylelint-disable no-descending-specificity */

.ui-select {
 display: flex;
 flex-direction: column;
 position: relative;
 width: 100%;

}

.ui-select__label {
 @include typography.font-style('text-sm', 'medium');

 color: var(--color-text-text-secondary-700);
 box-shadow: 0 1px 2px var(--color-effects-shadows-shadow-xs);
 margin-bottom: 6px;

}

.ui-select__label span {
 color: var(--color-brand-600);

}

.ui-select__button {
 @include typography.font-style('text-md', 'medium');

 align-items: center;
 background: var(--color-background-bg-primary);
 border: 1px solid var(--color-border-border-primary);
 border-radius: var(--radius-md);
 box-shadow: 0 1px 2px var(--color-effects-shadows-shadow-xs);
 color: var(--color-text-text-primary-900);
 cursor: pointer;
 display: flex;
 gap: 8px;
 height: 44px;
 padding: var(--spacing-md) var(--spacing-lg);
 text-align: left;
 width: 100%;

}

.ui-select--sm .ui-select__button {
 height: 40px;
 padding: var(--spacing-sm) var(--spacing-md);

}

.ui-select__button:hover:not(:disabled) {
 border-color: var(--color-gray-light-400);

}


.ui-select__button:focus-visible,
.ui-select__button[data-state='open'] {
 border-color: var(--color-border-border-brand);
 outline: 1px solid var(--color-border-border-brand) !important;
 outline-offset: 0;

}

.ui-select--error .ui-select__button {
 border-color: var(--color-border-border-error-subtle);

}

.ui-select--error .ui-select__button:focus-visible {
 border-color: var(--color-border-border-error-subtle);
 outline: 1px solid var(--color-border-border-error-subtle) !important;
 outline-offset: 0;

}

.ui-select__button:disabled {
 background: var(--color-background-bg-disabled-subtle);
 color: var(--color-text-text-disabled);
 cursor: not-allowed;

}

.ui-select--borderless .ui-select__button {
 border-color: transparent;
 box-shadow: none;

}

.ui-select--inverted .ui-select__button {
 background: var(--color-brand-800);
 border-color: var(--color-brand-700);
 color: var(--color-gray-dark-50);

}

.ui-select__prefix {
 align-items: center;
 display: flex;

}

.ui-select__value {
 flex: 1;
 min-width: 0;
 overflow: hidden;
 text-overflow: ellipsis;
 white-space: nowrap;

}

.ui-select__value[data-placeholder] {
 color: var(--color-text-text-placeholder);

}

.ui-select__chevron {
 transition: transform 0.2s ease;

}

.ui-select__button[data-state='open'] .ui-select__chevron {
 transform: rotate(180deg);

}

.ui-select__message {
 @include typography.font-style('text-sm', 'regular');

 color: var(--color-text-text-tertiary-600);
 margin: 6px 0 0;

}

.ui-select--error .ui-select__message {
 color: var(--color-error-600);

}

:global(.ui-select__options) {
 background: var(--color-background-bg-primary);
 border: 1px solid var(--color-border-border-secondary);
 border-radius: var(--radius-md);
 box-shadow: 0 12px 16px -4px var(--color-effects-shadows-shadow-lg-01),
  0 4px 6px -2px var(--color-effects-shadows-shadow-lg-02),
  0 2px 2px -1px var(--color-effects-shadows-shadow-lg-03);
 list-style: none;
 margin: 0;
 max-height: min(256px, var(--reka-select-content-available-height));
 overflow: hidden;
 padding: var(--spacing-xs) 0;
 width: var(--reka-select-trigger-width);
 z-index: 501;

}

:global(.ui-select__viewport) {
 max-height: 256px;
}

.ui-select__option {
 align-items: center;
 border-radius: var(--radius-sm);
 color: var(--color-text-text-secondary-700);
 cursor: pointer;
 display: flex;
 justify-content: space-between;
 padding: var(--spacing-md) 14px;

}

.ui-select__option[data-highlighted] {
 background: var(--color-background-bg-active);

}

.ui-select__option[data-state='checked'] {
 color: var(--color-text-text-secondary-700);

}

.ui-select__option[aria-disabled='true'] {
 color: var(--color-text-text-disabled);
 cursor: not-allowed;

}

.ui-select__option small {
 @include typography.font-style('text-sm', 'regular');

 color: var(--color-text-text-tertiary-600);
 display: block;

}

.ui-select__option strong {
 @include typography.font-style('text-md', 'medium');
}

.ui-select__empty {
 @include typography.font-style('text-sm', 'regular');

 color: var(--color-text-text-tertiary-600);
 padding: 10px;
 text-align: center;

}


</style>
